import { AxiosResponse } from 'axios';
import { getLocalISODateString } from '../common/dateUtils';
import { TaskComment, TaskCommentCreationAttributes } from '../types/Comment';
import { TaskActivityResponse } from '../types/TaskActivity';
import { CommentAttachment } from '../types/CommentAttachment';
import Task, { TaskStage } from '../types/Task';
import { TaskAttachment } from '../types/TaskAttachment';
import { TaskLink } from '../types/TaskLink';
import { TaskDependencies, TaskDependencyDirection } from '../types/TaskDependency';
import httpClient from './httpClient';
import { AddLabelsToTaskRequest, UpdateTaskLabelsRequest } from './types/label.types';

const TASK_BASE_ROUTE = '/v1/task';

/**
 * Creates a dummy placeholder ticket under provided workspace.
 * @param {number} workspaceId
 * @returns {Promise<AxiosResponse<Task>>}
 */
export async function createPlaceholderTask(
  workspaceId: number,
  epicId?: number,
  title?: string,
  planningSessionId?: number,
  statusId?: number,
  priority?: string,
  sprintId?: number,
  estimate?: number,
  storyPoints?: number,
  startDate?: string,
  deadline?: string,
  type?: string,
  parentTaskId?: number,
  assignee?: number,
  description?: string,
  stage?: TaskStage
): Promise<AxiosResponse<Task>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/placeholder`,
    data: {
      workspaceId,
      epicId,
      title,
      planningSessionId,
      statusId,
      priority,
      sprintId,
      estimate,
      storyPoints,
      startDate,
      deadline,
      type,
      parentTaskId,
      assignee,
      description,
      stage,
    },
  });
}

/**
 * Returns a list of tasks for provided space. Archived tasks are excluded by default
 * unless `stage: 'archive'` is explicitly passed.
 */
export async function getTasksForGivenSpace(spaceId: number, stage?: TaskStage): Promise<AxiosResponse<Task[]>> {
  const qs = new URLSearchParams({ spaceId: String(spaceId) });
  if (stage) qs.set('stage', stage);
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/?${qs.toString()}`,
  });
}

/**
 * One task by its per-space number. `spacePublicId` is the id from the URL, so this
 * can be called before the workspace lookup resolves the numeric space id.
 */
export async function getTaskByNumber(spacePublicId: string, taskNumber: number): Promise<AxiosResponse<Task>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/space/${spacePublicId}/number/${taskNumber}`,
  });
}

export async function updateTask(taskId: number, task: Partial<Task>): Promise<AxiosResponse<Task>> {
  const taskWithCorrectSchema = {
    ...task,
  };
  if ('key' in taskWithCorrectSchema) delete taskWithCorrectSchema.key;
  if ('subtasks' in taskWithCorrectSchema) delete taskWithCorrectSchema.subtasks;
  if ('TaskLabels' in taskWithCorrectSchema) delete taskWithCorrectSchema.TaskLabels;
  if ('BlockingEdges' in taskWithCorrectSchema) delete taskWithCorrectSchema.BlockingEdges;
  if ('BlockedByEdges' in taskWithCorrectSchema) delete taskWithCorrectSchema.BlockedByEdges;
  if ('isIncludedForChildContext' in taskWithCorrectSchema) delete taskWithCorrectSchema.isIncludedForChildContext;
  if ('isDisabledInFilter' in taskWithCorrectSchema) delete taskWithCorrectSchema.isDisabledInFilter;
  if ('isGroupContext' in taskWithCorrectSchema) delete taskWithCorrectSchema.isGroupContext;

  return httpClient({
    method: 'PUT',
    url: `${TASK_BASE_ROUTE}/${taskId}`,
    data: {
      task: { ...taskWithCorrectSchema, deadline: task.deadline ? getLocalISODateString(task.deadline) : null },
    },
  });
}

/**
 * Deletes a task with given ID.
 * @param taskId
 */
export async function deleteTask(taskId: number): Promise<AxiosResponse<boolean>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}`,
  });
}

export function updateTaskOrdering(position: number, taskId: number, sprintId: number, newStatusId: number) {
  return httpClient({
    method: 'PUT',
    url: `${TASK_BASE_ROUTE}/change_order/${taskId}`,
    data: {
      sprintId,
      position,
      newStatusId,
    },
  });
}

export function moveTaskToPlaning(taskId: number): Promise<AxiosResponse<Task>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/move_to_planning/${taskId}`,
  });
}

export function createTaskInSprint(
  sprintId: number,
  statusId: number,
  orderIndex?: number
): Promise<AxiosResponse<Task>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/sprint/${sprintId}/add_task`,
    data: {
      statusId,
      orderIndex,
    },
  });
}

export function addTaskComment(
  taskId: number,
  comment: TaskCommentCreationAttributes,
  tempAttachmentKeys?: string[]
): Promise<AxiosResponse<TaskComment>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments`,
    data: {
      ...comment,
      tempAttachmentKeys,
    },
  });
}

export function getTaskComments(taskId: number): Promise<AxiosResponse<TaskComment[]>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments`,
  });
}

export function getTaskActivity(taskId: number, cursor?: number): Promise<AxiosResponse<TaskActivityResponse>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/${taskId}/activity`,
    params: cursor ? { cursor } : undefined,
  });
}

export function updateTaskComment(
  taskId: number,
  commentId: number,
  content: string,
  mentionIds?: number[]
): Promise<AxiosResponse<TaskComment>> {
  return httpClient({
    method: 'PUT',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments/${commentId}`,
    data: {
      content,
      mentionIds,
    },
  });
}

export function deleteTaskComment(
  taskId: number,
  commentId: number
): Promise<AxiosResponse<{ message: string; deletedComment: TaskComment }>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments/${commentId}`,
  });
}

export function addCommentReaction(taskId: number, commentId: number, emoji: string): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments/${commentId}/reactions`,
    data: { emoji },
  });
}

export function removeCommentReaction(
  taskId: number,
  commentId: number,
  emoji: string
): Promise<AxiosResponse<unknown>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments/${commentId}/reactions`,
    data: { emoji },
  });
}

export function addTaskLink(taskId: number, link: string, name: string): Promise<AxiosResponse<TaskLink>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/links`,
    data: {
      link,
      name,
    },
  });
}

export function updateTaskLink(
  taskId: number,
  taskLinkId: number,
  link: string,
  name: string
): Promise<AxiosResponse<TaskLink>> {
  return httpClient({
    method: 'PUT',
    url: `${TASK_BASE_ROUTE}/${taskId}/links/${taskLinkId}`,
    data: { link, name },
  });
}

export function getTaskLinks(taskId: number): Promise<AxiosResponse<TaskLink[]>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/${taskId}/links`,
  });
}

export function deleteTaskLink(taskId: number, linkId: number): Promise<AxiosResponse<boolean>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/links/${linkId}`,
  });
}

export function deleteTaskLinks(taskId: number): Promise<AxiosResponse<boolean>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/links`,
  });
}

export function getTaskDependencies(taskId: number): Promise<AxiosResponse<TaskDependencies>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/${taskId}/dependencies`,
  });
}

export function addTaskDependency(
  taskId: number,
  relatedTaskId: number,
  type: TaskDependencyDirection
): Promise<AxiosResponse<{ id: number }>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/dependencies`,
    data: { relatedTaskId, type },
  });
}

export function deleteTaskDependency(taskId: number, dependencyId: number): Promise<AxiosResponse<{ id: number }>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/dependencies/${dependencyId}`,
  });
}

export function uploadTaskAttachment(taskId: number, file: File): Promise<AxiosResponse<TaskAttachment>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/attachments`,
    data: (() => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    })(),
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function uploadTaskDescriptionAttachment(taskId: number, file: File): Promise<AxiosResponse<TaskAttachment>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/description-attachments`,
    data: (() => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    })(),
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function getTaskAttachments(taskId: number): Promise<AxiosResponse<TaskAttachment[]>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/${taskId}/attachments`,
  });
}

export function deleteTaskAttachment(taskId: number, attachmentId: number): Promise<AxiosResponse<boolean>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/attachments/${attachmentId}`,
  });
}

export function deleteTaskAttachments(taskId: number): Promise<AxiosResponse<boolean>> {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/attachments`,
  });
}

export function getTaskSubtasks(taskId: number): Promise<AxiosResponse<Task[]>> {
  return httpClient({
    method: 'GET',
    url: `${TASK_BASE_ROUTE}/${taskId}/subtasks`,
  });
}

export function uploadTemporaryAttachment(taskId: number, file: File): Promise<AxiosResponse<CommentAttachment>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/temp-attachments`,
    data: (() => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    })(),
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function uploadCommentAttachment(
  taskId: number,
  commentId: number,
  file: File
): Promise<AxiosResponse<CommentAttachment>> {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/comments/${commentId}/attachments`,
    data: (() => {
      const formData = new FormData();
      formData.append('file', file);
      return formData;
    })(),
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

// Task-Label assignment functions
export const addLabelsToTask = async (taskId: number, data: AddLabelsToTaskRequest): Promise<AxiosResponse<void>> => {
  return httpClient({
    method: 'POST',
    url: `${TASK_BASE_ROUTE}/${taskId}/labels`,
    data,
  });
};

export const removeLabelFromTask = async (taskId: number, labelId: number): Promise<AxiosResponse<void>> => {
  return httpClient({
    method: 'DELETE',
    url: `${TASK_BASE_ROUTE}/${taskId}/label/${labelId}`,
  });
};

export const updateTaskLabels = async (taskId: number, data: UpdateTaskLabelsRequest): Promise<AxiosResponse<void>> => {
  return httpClient({
    method: 'PUT',
    url: `${TASK_BASE_ROUTE}/${taskId}/labels`,
    data,
  });
};
